import { useQuery } from '@apollo/client'
import React, { useState } from 'react'
import { Modal, Button, Row, Col, Form, InputGroup, Spinner } from 'react-bootstrap'
import { SEARCH_RESOURCES_CENTER } from '../../../gql/queries/resourceCenterQuery'
import { CampaignMediaUploadModal } from '../../campaigns/components/CampaignMediaUploadModal'
import { BACKEND_URL } from '../../../helpers/consts'
import { useIntl, FormattedMessage } from 'react-intl'
import { TEMPLATE_MESSAGES as M } from '../templateMessages'

// Get backend URL based on environment
const getBackendUrl = () => {
    return BACKEND_URL
}

interface MediaManagerModalProps {
    isOpen: boolean
    onClose: () => void
    onSelect: (url: string) => void
}

export const MediaManagerModal: React.FC<MediaManagerModalProps> = ({
    isOpen,
    onClose,
    onSelect,
}) => {
    const intl = useIntl()
    const [searchTerm, setSearchTerm] = useState('')
    const [showUploadModal, setShowUploadModal] = useState(false)

    // Query to fetch images (only campaign media)
    const { data, loading, refetch } = useQuery(SEARCH_RESOURCES_CENTER, {
        variables: {
            name: searchTerm,
            resourceType: 'image', // We only want images for this picker
            isActive: true,
            isCampaignMedia: true, // Only show campaign media, not Resource Center files
        },
        fetchPolicy: 'network-only',
    })

    const resources = data?.resourcesCenterAdvancedSearch || []

    // Filter further just in case the backend query isn't strict (though we passed resourceType)
    // And ensure we only show things with a valid URL or File property
    const images = resources.filter((r: any) => {
        // If resourceType is explicitly image, or file extension looks like an image
        // Note: resourceType 'image' might not exist in the defined enums yet, so we'll check extensiosn too
        const isImage = r.resourceType === 'image' ||
            (r.file && /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(r.file))
        return isImage
    })

    // Helper to get actual URL
    const getUrl = (resource: any) => {
        // If there's an explicit URL field, use it (already absolute)
        if (resource.url) return resource.url

        // If there's a file field, it might be a relative path
        if (resource.file) {
            // Check if it's already an absolute URL
            if (resource.file.startsWith('http://') || resource.file.startsWith('https://')) {
                return resource.file
            }

            // Otherwise, construct absolute URL using backend base URL
            const backendUrl = getBackendUrl()
            // Remove trailing slash from backend URL and ensure file path starts with /
            const cleanBackendUrl = backendUrl.replace(/\/$/, '')
            const cleanFilePath = resource.file.startsWith('/') ? resource.file : `/${resource.file}`
            return `${cleanBackendUrl}${cleanFilePath}`
        }

        return ''
    }

    const handleSelect = (resource: any) => {
        const url = getUrl(resource)
        if (url) {
            onSelect(url)
            onClose()
        }
    }

    return (
        <>
            <Modal show={isOpen} onHide={onClose} size='xl' centered scrollable>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className='bi bi-images me-2'></i> <FormattedMessage {...M.mediaTitle} />
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className='p-0'>
                    <div className='d-flex flex-column h-100'>
                        {/* Toolbar */}
                        <div className='p-3 border-bottom bg-light d-flex justify-content-between align-items-center'>
                            <InputGroup style={{ maxWidth: '300px' }}>
                                <InputGroup.Text>
                                    <i className='bi bi-search'></i>
                                </InputGroup.Text>
                                <Form.Control
                                    placeholder={intl.formatMessage(M.mediaSearch)}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </InputGroup>

                            <Button variant='primary' onClick={() => setShowUploadModal(true)}>
                                <i className='bi bi-cloud-upload me-2'></i> <FormattedMessage {...M.mediaUploadNew} />
                            </Button>
                        </div>

                        {/* Gallery Grid */}
                        <div className='p-4 bg-white' style={{ minHeight: '400px' }}>
                            {loading ? (
                                <div className='d-flex justify-content-center align-items-center h-100 p-5'>
                                    <Spinner animation='border' variant='primary' />
                                </div>
                            ) : images.length === 0 ? (
                                <div className='text-center py-5 text-muted'>
                                    <i className='bi bi-card-image fs-1 mb-3 d-block'></i>
                                    <h5><FormattedMessage {...M.mediaEmpty} /></h5>
                                    <p><FormattedMessage {...M.mediaEmptyHint} /></p>
                                    <Button variant='outline-primary' onClick={() => setShowUploadModal(true)}>
                                        <FormattedMessage {...M.mediaUploadNow} />
                                    </Button>
                                </div>
                            ) : (
                                <Row xs={2} md={3} lg={4} xl={5} className='g-3'>
                                    {images.map((img: any) => {
                                        const src = getUrl(img)
                                        return (
                                            <Col key={img.id}>
                                                <div
                                                    className='card h-100 cursor-pointer shadow-sm border-0 media-card'
                                                    onClick={() => handleSelect(img)}
                                                    style={{ transition: 'transform 0.2s', cursor: 'pointer' }}
                                                    onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                                                    onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                                >
                                                    <div
                                                        className='card-img-top bg-light d-flex align-items-center justify-content-center overflow-hidden'
                                                        style={{ height: '150px' }}
                                                    >
                                                        {src ? (
                                                            <img
                                                                src={src}
                                                                alt={img.name}
                                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                            />
                                                        ) : (
                                                            <i className='bi bi-image text-muted fs-1'></i>
                                                        )}
                                                    </div>
                                                    <div className='card-body p-2'>
                                                        <p className='card-text text-truncate small fw-bold mb-0' title={img.name}>
                                                            {img.name}
                                                        </p>
                                                        <small className='text-muted' style={{ fontSize: '0.75rem' }}>
                                                            {new Date(img.created).toLocaleDateString()}
                                                        </small>
                                                    </div>
                                                </div>
                                            </Col>
                                        )
                                    })}
                                </Row>
                            )}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant='secondary' onClick={onClose}>
                        <FormattedMessage {...M.cancel} />
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Upload Modal (Campaign Media) */}
            {showUploadModal && (
                <CampaignMediaUploadModal
                    isOpen={showUploadModal}
                    onClose={() => setShowUploadModal(false)}
                    refetch={refetch}
                />
            )}
        </>
    )
}
